// Server-derived direct requests -> the view models the request surfaces render.
//
// bead hf-e6-commitments-coverage-udt.1.
//
// ── WHAT THIS MODULE IS NOT ALLOWED TO DECIDE ──────────────────────────────────
//
// WHETHER AN ASK IS STILL OPEN. That is `convex/domain/requests.ts:isOpen`, applied
// server-side on every read. The browser may hide a server-open ask after its persisted
// deadline using elapsed monotonic time anchored to the server's `evaluatedAt`; it may
// never add an ask or reopen one. `myRequests` remains the authority and this module
// only carries the two server facts into the presentation model.
//
// WHETHER ANYTHING IS COVERED. There is no coverage in the wire type and none is
// invented here. The whole point of the bead is that an ask is not a fraction of a
// commitment, and a view model that computed "nearly covered" from a pending ask would
// reintroduce exactly that.
//
// ── WHAT IT DOES DECIDE: WORDS AND ORDER ───────────────────────────────────────
//
// Local-time labels in the CIRCLE's zone (invariant 9) — never the device's, because a
// helper reading their ask from a holiday must see the hours the family means.

import { instant, localDateOf, timeZone, type TimeZoneId } from "@herefor/domain-kernel";
import type { requests as contracts } from "@herefor/contracts";

import { formatWindowInZone } from "../plan/planData";

/** One ask, as the person who was asked reads it. */
export interface AskView {
  readonly directRequestId: string;
  readonly supportNeedId: string;
  readonly label: string;
  /** `/c/<ref>/needs/<id>`, or null when the surface has no route to offer. */
  readonly href: string | null;
  readonly windowLabel: string;
  readonly dateLabel: string;
  /** What a Yes will bind to. Travels back with the answer, untouched (REQ-006). */
  readonly needRevision: number;
  readonly requestedByName: string;
  /** Persisted server deadline, used only to remove an action after it passes. */
  readonly expiresAt: number;
  /** Server time at the query read, anchoring monotonic elapsed time in the browser. */
  readonly evaluatedAt: number;
  /** The deadline, as a date. So the ask visibly has a horizon (REQ-009). */
  readonly expiresOnLabel: string;
}

export interface AskContext {
  readonly timeZone: string;
  /** Builds need routes. An opaque identifier, never anything about a person. */
  readonly publicPlanRef?: string;
}

export function toAskViews(
  asks: readonly contracts.PendingRequestView[],
  context: AskContext,
): readonly AskView[] {
  const zone = timeZone(context.timeZone);
  return asks.map((ask) => toAskView(ask, zone, context));
}

export function toAskView(
  ask: contracts.PendingRequestView,
  zone: TimeZoneId,
  context: AskContext,
): AskView {
  return {
    directRequestId: ask.directRequestId,
    supportNeedId: ask.supportNeedId,
    label: ask.label,
    href:
      context.publicPlanRef === undefined
        ? null
        : `/c/${context.publicPlanRef}/needs/${ask.supportNeedId}`,
    windowLabel: formatWindowInZone(zone, instant(ask.window.startsAt), instant(ask.window.endsAt)),
    dateLabel: localDateOf(instant(ask.window.startsAt), zone),
    needRevision: ask.needRevision,
    requestedByName: ask.requestedByName,
    expiresAt: ask.expiresAt,
    evaluatedAt: ask.evaluatedAt,
    expiresOnLabel: localDateOf(instant(ask.expiresAt), zone),
  };
}

/**
 * The idempotency key for one answer.
 *
 * Derived from the request, the revision and the decision rather than random, exactly
 * as the recheck flow derives its own: a retry of the same answer must replay, while
 * a genuinely different answer — or the same answer to a newer revision — must be a
 * new write. A random key would make every retry a fresh mutation, which is the
 * failure retry-safety exists to prevent.
 */
export function answerKey(ask: AskView, response: "accept" | "decline"): string {
  return `ask-${ask.directRequestId}-${String(ask.needRevision)}-${response}`;
}

/** The idempotency key for one ask. Same reasoning as `answerKey`. */
export function sendKey(supportNeedId: string, personId: string, needRevision: number): string {
  return `send-ask-${supportNeedId}-${personId}-${String(needRevision)}`;
}
